import { useQuery } from '@apollo/client';
import React, { useMemo, useRef } from 'react';
import { css, styled } from 'styles';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { TalkOnboardingModal } from 'Talk/components/TalkOnboardingModal';
import { useLocation } from 'react-router';
import useIsManagerExperienceActive from 'Home/Managers/useIsManagerExperienceActive';
import { LINE_MANAGERS_CATEGORY } from 'Home/Managers/constants';
import YourCourses from 'Home/PersonalisedToday/YourCourses/YourCourses';
import { getUserLocale } from 'i18n/getUserLocale';
import { FEEDBACK_LOCALES } from 'i18n/feedbackLocales';
import { isMSTeams } from 'utils/MSTeams/MSTeamsHelpers';
import { extraSmall, small } from '@unmind/design-system-components-web';
import { useSetBrazeLanguage } from '../../App/braze/setBrazeLanguage';
import { HiddenOnMobile } from '../../Navigation/AuthenticatedPage';
import Container from '../../Shared/Container';
import LoadingIndicator from '../../Shared/LoadingIndicator';
import SectionError from '../../Shared/SectionError';
import ManagersToday from '../Managers/ManagersToday';
import Header from './Header/Header';
import DailyBoostBanner from './DailyBoostBanner';
import { PERSONALISED_TODAY_CONTENT_QUERY } from './personalisedTodayQueries';
import {
  PersonalisedTodayContent,
  PersonalisedTodayContent_user_dailySelection_beforeWork,
  PersonalisedTodayContent_user_dailySelection_duringWork,
  PersonalisedTodayContent_user_dailySelection_afterWork,
  PersonalisedTodayContentVariables,
} from './__generated__/PersonalisedTodayContent';
import ChatbotBanner from './ChatbotBanner/ChatbotBanner';
import SupportForYou from './SupportForYou/SupportForYou';
import ShareFeedback from './ShareFeedback/ShareFeedback';
import ToolsForYourDay from './ToolsForYourDay/ToolsForYourDay';
import { calculateSupportFeatureCount, getToolsForYourDay } from './utils';

const HomeScreenHeadingContainer = styled(HiddenOnMobile)<{
  $isMSTeams: boolean;
}>`
  ${({ $isMSTeams }) =>
    $isMSTeams &&
    css`
      display: flex;
      justify-content: center;
    `}

  ${extraSmall(css`
    justify-content: center;
  `)}
`;

const TodayWrapper = styled(Container)`
  padding-bottom: 26px;
  padding-top: 80px;
`;

export const HomeScreenSection = styled.section``;

export const HomeScreenContainer = styled.div<{
  $isNovaEnabled: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 80px;
  padding-top: 0;
  ${({ $isNovaEnabled }) =>
    small(css`
      padding-top: ${$isNovaEnabled ? '80px' : '0'};
    `)}
`;

const ErrorWrapper = styled.div`
  margin-top: 56px;
`;

export const ContentCardWrapper = styled.div``;

export type DailySelectionTool =
  | PersonalisedTodayContent_user_dailySelection_beforeWork
  | PersonalisedTodayContent_user_dailySelection_duringWork
  | PersonalisedTodayContent_user_dailySelection_afterWork;

const extractDataFieldsSafely = (
  data: PersonalisedTodayContent | undefined,
) => ({
  assetToken: data?.getAssetToken?.assetToken,
  dailyBoostShort: data?.dailyBoostShort,
  lineManagerCategorySlug:
    data?.homeAssembly?.featuredCategory?.slug ?? LINE_MANAGERS_CATEGORY,
  lineManagerFeaturedContent: data?.homeAssembly?.featuredContent ?? [],
  featuredHeroCourses: data?.homeAssembly?.featuredHeroCourses ?? [],
  yourCoursesData: data?.user?.coursesInProgress,
  beforeWork: data?.user?.dailySelection?.beforeWork ?? [],
  duringWork: data?.user?.dailySelection?.duringWork ?? [],
  afterWork: data?.user?.dailySelection?.afterWork ?? [],
  userFeatures: data?.user?.features,
  firstName: data?.user?.firstName,
});

const TodayScreenContent = () => {
  const userDateTimeISOFormat = useMemo(() => new Date().toISOString(), []);
  const isNovaPhase2Enabled = useFeatureFlag(FEATURE_FLAGS.NOVA_HOME_PHASE_2);
  const isNovaEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_CHATBOT);
  const userLocale = getUserLocale();

  const { isManagerExperienceActive, loading: isManagerExperienceLoading } =
    useIsManagerExperienceActive();

  const { data, loading, error, refetch } = useQuery<
    PersonalisedTodayContent,
    PersonalisedTodayContentVariables
  >(PERSONALISED_TODAY_CONTENT_QUERY, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    returnPartialData: false,
    notifyOnNetworkStatusChange: true,
    variables: {
      userLocalDateTime: userDateTimeISOFormat,
    },
  });

  const {
    assetToken,
    dailyBoostShort,
    lineManagerCategorySlug,
    lineManagerFeaturedContent,
    featuredHeroCourses,
    yourCoursesData,
    beforeWork,
    duringWork,
    afterWork,
    userFeatures,
    firstName,
  } = extractDataFieldsSafely(data);

  const toolsForYourDay = getToolsForYourDay(beforeWork, duringWork, afterWork);

  const supportFeatureCount = calculateSupportFeatureCount(
    isManagerExperienceActive,
    userFeatures,
  );

  if (loading || isManagerExperienceLoading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return (
      <ErrorWrapper>
        <SectionError
          onRetry={async () =>
            refetch({ userLocalDateTime: userDateTimeISOFormat })
          }
        />
      </ErrorWrapper>
    );
  }

  if (isManagerExperienceActive && !isNovaPhase2Enabled) {
    return (
      <ManagersToday
        assetToken={assetToken}
        lineManagerCategorySlug={lineManagerCategorySlug}
        featuredContent={lineManagerFeaturedContent}
        featuredHeroCourses={featuredHeroCourses}
        dailyBoost={dailyBoostShort}
      />
    );
  }

  return (
    <HomeScreenContainer $isNovaEnabled={isNovaEnabled ?? false}>
      {isNovaEnabled ? (
        <ChatbotBanner firstName={firstName} />
      ) : (
        <HomeScreenHeadingContainer $isMSTeams={isMSTeams()}>
          <Header firstName={firstName} />
        </HomeScreenHeadingContainer>
      )}
      {userFeatures && supportFeatureCount >= 2 && (
        <SupportForYou
          userFeatures={userFeatures}
          supportFeatureCount={supportFeatureCount}
          isManagerExperienceActive={Boolean(
            isManagerExperienceActive && isNovaPhase2Enabled,
          )}
        />
      )}
      {yourCoursesData && (
        <YourCourses
          courses={yourCoursesData.filter(course => course.totalDays !== 1)}
          assetToken={assetToken}
        />
      )}
      {dailyBoostShort && (
        <DailyBoostBanner tool={dailyBoostShort} assetToken={assetToken} />
      )}
      {toolsForYourDay.length > 0 && (
        <ToolsForYourDay tools={toolsForYourDay} assetToken={assetToken} />
      )}
      {FEEDBACK_LOCALES.includes(userLocale) && <ShareFeedback />}
    </HomeScreenContainer>
  );
};

const PersonalisedTodayScreen = () => {
  useSetBrazeLanguage();
  const modalCloseReturnRef = useRef<HTMLDivElement>(null);

  const { state: locationState } = useLocation();
  const isFirstLogin =
    history.state?.state?.isFirstLogin || locationState?.isFirstLogin;

  return (
    <TodayWrapper ref={modalCloseReturnRef}>
      <TodayScreenContent />
      {isFirstLogin ? (
        <TalkOnboardingModal returnFocusTo={modalCloseReturnRef} />
      ) : null}
    </TodayWrapper>
  );
};

export default PersonalisedTodayScreen;
